import React, { useLayoutEffect } from 'react';
import { Bounce, ToastContainer } from 'react-toastify';
import type { ToastTransitionProps } from 'react-toastify';

import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { DEFAULT_TOAST_AUTO_CLOSE_MS } from './toast-config';
import './toast-overrides.css';

const FallbackTransition: React.FC<ToastTransitionProps> = ({ children }) => (
  <>{children}</>
);
const toastTransition = Bounce ?? FallbackTransition;

export default function ToastRoot() {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));

  useLayoutEffect(() => {
    const root = document.documentElement;
    const { palette, typography } = theme;

    root.style.setProperty('--app-toast-bg', palette.background.paper);
    root.style.setProperty('--app-toast-text', palette.text.primary);
    root.style.setProperty('--app-toast-close', palette.text.secondary);
    root.style.setProperty('--app-toast-success', palette.success.main);
    root.style.setProperty('--app-toast-error', palette.error.main);
    root.style.setProperty('--app-toast-warning', palette.warning.main);
    root.style.setProperty('--app-toast-border-muted', palette.border.main);
    root.style.setProperty('--app-toast-font', typography.fontFamily as string);
    root.style.setProperty('--app-toast-width', 'min(100vw - 32px, 320px)');
    root.style.setProperty(
      '--app-toast-inset-top',
      `calc(env(safe-area-inset-top, 0px) + var(--vv-offset-top, 0px) + ${theme.spacing(2)})`,
    );

    return () => {
      root.style.removeProperty('--app-toast-bg');
      root.style.removeProperty('--app-toast-text');
      root.style.removeProperty('--app-toast-close');
      root.style.removeProperty('--app-toast-success');
      root.style.removeProperty('--app-toast-error');
      root.style.removeProperty('--app-toast-warning');
      root.style.removeProperty('--app-toast-border-muted');
      root.style.removeProperty('--app-toast-font');
      root.style.removeProperty('--app-toast-width');
      root.style.removeProperty('--app-toast-inset-top');
    };
  }, [theme]);

  return (
    <ToastContainer
      className="app-toast-root"
      position={isMobileLayout ? 'top-center' : 'bottom-left'}
      autoClose={DEFAULT_TOAST_AUTO_CLOSE_MS}
      hideProgressBar
      newestOnTop={!isMobileLayout}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss={false}
      draggable={false}
      pauseOnHover
      theme="light"
      transition={toastTransition}
    />
  );
}
